import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import * as yup from 'yup';
import { ApplyAdministrativeFeesDto } from '../../../types/ApplyAdministrativeFeesDto';
import { generateYears } from '../../../common/Utils';
import { Button } from '../../../components';
import { invoiceService } from '../../../services/InvoiceService';
import { toast } from 'react-toastify';

interface AdministrativeFeesFormProps {
    onSave: () => void;
    onCancel: () => void;
}

const AdministrativeFeesForm = (props: AdministrativeFeesFormProps) => {
    const schema = yup
        .object({
            fiscalYear: yup.string().required(),
            lateFee: yup.number().required(),
            notificationEmail: yup.string().email().optional(),
        })
        .defined();

    const { handleSubmit, register, control, formState: { errors } } = useForm({
        resolver: yupResolver(schema as any),
        defaultValues: {
            fiscalYear: new Date().getFullYear(),
            lateFee: 200,
            notificationEmail: 'receivables@caahep.org',
        },
    });

    const onSubmit = (formData) => {
        const toastId = toast.info('Applying administrative fees...');

        invoiceService
            .applyAdministrativeFees(formData)
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Late fees application started successfully! You will receive an email when processing is complete.',
                });
                props.onSave();
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error starting late fees application',
                });
            });
    };

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
                <Label for="fiscalYear">Fiscal Year</Label>
                <Controller
                    name="fiscalYear"
                    control={control}
                    render={({ field }) => (
                        <Input type="select" {...field} value={field.value ?? ''}>
                            <option value={''}>Select...</option>
                            {generateYears().map((ct) => (
                                <option key={ct} value={ct}>
                                    {ct}
                                </option>
                            ))}
                        </Input>
                    )}
                />
                {errors?.fiscalYear?.message && (
                    <span className={'text-danger'}>{errors.fiscalYear.message}</span>
                )}
            </FormGroup>
            <FormGroup>
                <Label for="lateFee">Amount</Label>
                <Controller
                    name="lateFee"
                    control={control}
                    render={({ field }) => (
                        <Input type="number" {...field} value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)} />
                    )}
                />
                {errors?.lateFee?.message && (
                    <span className={'text-danger'}>{errors.lateFee.message}</span>
                )}
            </FormGroup>
            <FormGroup>
                <Label for="notificationEmail">Notification Email (optional)</Label>
                <Controller
                    name="notificationEmail"
                    control={control}
                    render={({ field }) => (
                        <Input
                            type="email"
                            {...field}
                            value={field.value ?? ''}
                            placeholder="Email address to notify when processing is complete"
                        />
                    )}
                />
            </FormGroup>
            <div>
                <Button type="button" color={'link'} onClick={props.onCancel}>
                    Cancel
                </Button>
                <Button type="submit">Submit</Button>
            </div>
        </Form>
    );
};

export default AdministrativeFeesForm;
